import {B2BScreenParam, Order, OrderVerifyStatus} from '@buncha/B2B/types';
import {Box, HBox} from '@buncha/components/core/view';
import {CustomText, PSmallText} from '@buncha/components/core/text';
import React, {useCallback, useMemo} from 'react';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {ActivityIndicator} from 'react-native';
import AddToCartIcon from '@buncha/components/core/icon/AddToCartIcon';
import FailedItemAccordion from '../../FailedItemAccordion';
import FailedItemCellFooter from '../../FailedItemCellFooter';
import {HTouchable} from '@buncha/components/core/touchable';
import {HomeScreens} from '@buncha/home/navigation/types';
import If from '@buncha/components/conditional/If';
import ImageStack from '../../ImageStack';
import LoadingItemCellFooter from '../../LoadingItemCellFooter';
import {Modules} from '@buncha/utils/types';
import OrderIdPill from '../../OrderIdPill';
import {OrderItems} from '@buncha/batchDetails/types';
import {PButton} from '@buncha/components/core/Button';
import SuccessItemCellFooter from '../../SuccessItemCellFooter';
import UserHeader from '../../UserHeader';
import {b2bNav} from '@buncha/B2B/navigation';
import {styles} from './styles';
import {useGetLocale} from '@buncha/localization/hooks';
import {useRoute} from '@react-navigation/native';

interface FooterText {
  title: string;
  subText: string;
}

interface CheckoutItemProps {
  status: OrderVerifyStatus;
  order?: Order;
  hideItemButton?: boolean;
  loadingFooter: FooterText;
  failedFooter: FooterText;
  successFooterText?: string;
  buttonTitle: string;
  onButtonClick: (orderId: number) => void;
  btnLoading?: boolean;
  defectedItem?: OrderItems[];
  btnLoadingTitle: string;
  leftComponent?: React.ReactNode;
  addItemChange?: () => void;
  footerRightComponent?: React.ReactNode;
}

const CheckoutItem: React.FC<CheckoutItemProps> = props => {
  const {
    hideItemButton,
    loadingFooter,
    successFooterText,
    failedFooter,
    buttonTitle,
    onButtonClick,
    order,
    status,
    btnLoading,
    defectedItem,
    btnLoadingTitle,
    leftComponent,
    addItemChange,
    footerRightComponent,
  } = props;
  const theme = useColorThemeType();
  const muliFont = useFontTheme('muli');
  const {body} = useGetLocale(Modules.B2B, HomeScreens.B2B) ?? {};
  const inVerification = [
    OrderVerifyStatus.pending,
    OrderVerifyStatus.validationFailed,
    OrderVerifyStatus.validationDone,
  ];
  const verificationStatusFailed =
    status === OrderVerifyStatus.validationFailed;
  const isUpdationInProgress = status === OrderVerifyStatus.updationInProgress;
  const isUpdationDone =
    status === OrderVerifyStatus.updationDone ||
    status === OrderVerifyStatus.scanSuccessful;
  const updationFailed = status === OrderVerifyStatus.updationFailed;
  const footerShown =
    isUpdationInProgress ||
    isUpdationDone ||
    (verificationStatusFailed && defectedItem?.length) ||
    updationFailed;
  const footerBoxValue = useMemo(
    () => (footerShown ? 0 : undefined),
    [footerShown],
  );
  const buyer = order?.buyer;
  const buyerFullName =
    (buyer?.firstName ?? '') + ' ' + (buyer?.lastName ?? '');
  const orderItems = order?.items;
  const orderItemCount = orderItems?.length ? orderItems?.length : 0;
  const orderId = order?.id;
  const routeParams = useRoute()?.params as B2BScreenParam | undefined;
  const runId = Number(routeParams?.runId) || 0;
  const itemImageUrls = useMemo(() => {
    return orderItems?.map(item => item.itemImageUrl);
  }, [orderItems]);

  const onClick = useCallback(() => {
    if (orderId) onButtonClick(orderId);
  }, [onButtonClick, orderId]);

  return (
    <Box>
      <Box
        style={[
          styles.container,
          {
            borderColor: theme?.text.gray[300],
            borderBottomWidth: footerBoxValue,
            borderBottomRightRadius: footerBoxValue,
            borderBottomLeftRadius: footerBoxValue,
          },
        ]}>
        <If condition={order?.externalId}>
          <Box
            style={[
              styles.externalPillcontainer,
              {
                backgroundColor: theme?.background.gray[200],
              },
            ]}>
            <CustomText
              style={{
                color: theme?.text.gray[100],
                fontFamily: muliFont[700].normal,
              }}>
              External Order ID: {order?.externalId}
            </CustomText>
          </Box>
        </If>
        <Box style={styles.headerStyle}>
          <UserHeader
            buyerName={buyerFullName}
            subText={body?.itemsPicked?.replace(
              '{filterItems}',
              orderItemCount,
            )}
            leftComponent={
              <Box>
                <OrderIdPill
                  text={body?.orderId?.replace('{orderId}', orderId)}
                />
                <If condition={inVerification.includes(status)}>
                  <HTouchable
                    onPress={addItemChange}
                    style={[
                      styles.addContainer,
                      {
                        borderColor: theme?.background.blue[100],
                      },
                    ]}>
                    <AddToCartIcon
                      width={14}
                      height={14}
                      viewBox="0 0 21 21"
                      fill={theme?.text.blue[100]}
                    />
                    <PSmallText
                      style={[
                        styles.addText,
                        {
                          color: theme?.text.blue[100],
                          fontFamily: muliFont[700].normal,
                        },
                      ]}>
                      Add Item
                    </PSmallText>
                  </HTouchable>
                </If>
              </Box>
            }
          />
        </Box>
        <HBox style={styles.imageContainer}>
          <ImageStack imageUrls={itemImageUrls ?? []} />
          <HBox>
            <If condition={itemImageUrls?.length}>
              <HTouchable
                style={styles.allContainer}
                onPress={() =>
                  b2bNav.B2BOrderInfo({
                    runId: runId,
                    screenTitle:
                      status === OrderVerifyStatus.updationFailed
                        ? body?.returnAll
                        : body?.orderInfo,
                    status,
                    orderId: orderId,
                  })
                }>
                <CustomText
                  style={{
                    fontFamily: muliFont[900].normal,
                    color: theme?.text.blue[100],
                  }}>
                  {body?.viewAll}
                </CustomText>
              </HTouchable>
            </If>
          </HBox>
        </HBox>
        <If condition={!hideItemButton}>
          <PButton
            title={buttonTitle}
            style={styles.actionButton}
            bgColor={'transparent'}
            titleColor={theme?.text.blue[100]}
            disabledBgColor={'transparent'}
            titleDisableColor={theme?.text.blue[100]}
            borderDisableColor={theme?.text.blue[100]}
            borderColor={theme?.text.blue[100]}
            borderWidth={1}
            disabled={btnLoading}
            isLoading={btnLoading}
            changeStyleOnLoading
            leftComponent={leftComponent}
            loadingText={btnLoadingTitle}
            loadingIndicator={!isUpdationDone && <ActivityIndicator />}
            onPress={onClick}
          />
        </If>
      </Box>
      <If condition={isUpdationInProgress}>
        <LoadingItemCellFooter
          title={loadingFooter.title}
          subtext={loadingFooter.subText}
        />
      </If>
      <If condition={isUpdationDone}>
        <SuccessItemCellFooter
          title={successFooterText ?? ''}
          rightComponent={footerRightComponent}
        />
      </If>
      <If
        condition={
          (verificationStatusFailed && defectedItem?.length) || updationFailed
        }>
        <If condition={defectedItem?.length}>
          <Box
            style={[
              styles.accordionStyle,
              {
                borderColor: theme?.text.gray[300],
              },
            ]}>
            <FailedItemAccordion
              defectedItem={defectedItem ?? []}
              orderId={orderId ?? 0}
            />
          </Box>
        </If>
        <FailedItemCellFooter
          title={failedFooter.title}
          subTitle={failedFooter.subText}
        />
      </If>
    </Box>
  );
};

export default React.memo(CheckoutItem);
